// CARO. Casa de estilo — locale resolution for CONTENT (products, articles).
// Base content is Spanish (es). For any other locale we overlay the available
// translation field-by-field, falling back to the Spanish value when a given
// field has no translation. Pure functions (no DOM/Worker globals): safe in the
// shared layer and callable from client components with the active locale.

import type { Article, Product } from '@/types';

/** Overlay `patch` onto `base`, ignoring undefined/null patch values. */
function overlay<T extends object>(base: T, patch: Partial<T> | undefined): T {
  if (!patch) return base;
  const out = { ...base };
  for (const key of Object.keys(patch) as (keyof T)[]) {
    const v = patch[key];
    if (v !== undefined && v !== null) out[key] = v as T[keyof T];
  }
  return out;
}

/**
 * Return the product with its textual fields resolved for `locale`.
 * `es` (or a missing translation) returns the base product unchanged.
 */
export function localizeProduct(product: Product, locale: string): Product {
  if (locale === 'es') return product;
  const tr = product.metadata?.i18n?.[locale];
  if (!tr) return product;
  return {
    ...product,
    name: tr.name ?? product.name,
    subtitle: tr.subtitle ?? product.subtitle,
    description: tr.description ?? product.description,
    caroNote: tr.caroNote ?? product.caroNote,
    metadata: overlay(product.metadata, {
      size: tr.size,
      edition: tr.edition,
      shippingNote: tr.shippingNote,
      preorderNote: tr.preorderNote,
      seoTitle: tr.seoTitle,
      benefits: tr.benefits,
      howToUse: tr.howToUse,
      ingredients: tr.ingredients,
      inci: tr.inci,
      warnings: tr.warnings,
      findStory: tr.findStory,
      findMeasurements: tr.findMeasurements,
    }),
  };
}

/**
 * Return the article with its textual fields resolved for `locale`.
 * `es` (or a missing translation) returns the base article unchanged.
 */
export function localizeArticle(article: Article, locale: string): Article {
  if (locale === 'es') return article;
  const tr = article.i18n?.[locale];
  if (!tr) return article;
  return {
    ...article,
    title: tr.title ?? article.title,
    excerpt: tr.excerpt ?? article.excerpt,
    body: tr.body ?? article.body,
    seoTitle: tr.seoTitle ?? article.seoTitle,
    seoDescription: tr.seoDescription ?? article.seoDescription,
  };
}
